// /src/i18n/useTranslation.js
// React hook and context provider for the OurNet translation system.
//
// All translation data lives in React state (no module-level caches).
// This ensures React re-renders components whenever translations change.
//
// Usage in components:
//   import { useTranslation } from '../../i18n/useTranslation';
//   const { t } = useTranslation('auth');
//   <h2>{t('login_title')}</h2>           // → looks up 'auth.login_title'
//   <p>{t('common:cancel')}</p>           // → cross-namespace: looks up 'common.cancel'
//   <p>{t('welcome', { name })}</p>       // → interpolation
//
// Provider (App.js renders it, passing the member's id and the country in view):
//   <TranslationProvider userId={currentUser?.id} countryCode={countryInView}>…</TranslationProvider>

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchEnglishStrings, fetchTranslations, resolveTranslation } from './translationService';
import { resolveLocale, toFormattingLocale, FALLBACK_LOCALE } from './resolveLanguage';
import { getLanguageSettings, getMyPreferredLocale, setMyPreferredLocale } from '../data/languageService';
import { DEFAULT_COUNTRY_CODE } from '../data/countriesService';

// A visitor's explicit choice, remembered in this browser only (R3). A member's choice lives on
// their account (users.preferred_locale) and is never written here, so it cannot leak to whoever
// uses the browser next.
const VISITOR_CHOICE_KEY = 'ournet_language';

const readVisitorChoice = () => {
  try { return localStorage.getItem(VISITOR_CHOICE_KEY) || null; } catch { return null; }
};

// ============================================================================
// CONTEXT
// ============================================================================

const TranslationContext = createContext({
  locale: FALLBACK_LOCALE,
  formattingLocale: toFormattingLocale(FALLBACK_LOCALE),
  english: {},
  translations: {},
  changeLocale: () => {},
  languages: [],
  countryInView: DEFAULT_COUNTRY_CODE,
  ready: false
});

// ============================================================================
// PROVIDER
// ============================================================================

/**
 * Decides the language (Roadmap #85, docs/briefs/2026-10-01-country-default-language.md +
 * addenda 1 and 2) and loads its strings.
 *
 * @param {string|null} userId - the signed-in member's id; null/undefined for a visitor
 * @param {string} countryCode - the country in view (R1), derived once in App.js
 */
export function TranslationProvider({ children, userId = null, countryCode = DEFAULT_COUNTRY_CODE }) {
  // undefined = still loading; null = the read failed; else { languages, countryDefaults }
  const [settings, setSettings] = useState(undefined);
  const [visitorChoice, setVisitorChoice] = useState(readVisitorChoice);
  // The member's choice, for the member it was read for: { userId, locale } once read.
  const [memberChoice, setMemberChoice] = useState(null);
  const [english, setEnglish] = useState({});
  const [translations, setTranslations] = useState({});
  const [ready, setReady] = useState(false);
  // The member id for whom the menu was used while their account was still being read: the
  // adoption below must not then overwrite that fresh choice with this browser's old one.
  const changedDuringReadRef = useRef(null);

  // The language list and the country defaults — read once.
  useEffect(() => {
    let cancelled = false;
    getLanguageSettings().then(result => { if (!cancelled) setSettings(result); });
    return () => { cancelled = true; };
  }, []);

  // A member's choice follows the account (R3). Signing in after choosing as a visitor keeps the
  // visitor's choice if the account has none — but only when the read SUCCEEDED and found none: a
  // failed read is not "no choice", and treating it as one would overwrite the member's own
  // language with whatever this browser holds.
  useEffect(() => {
    if (!userId) {
      setMemberChoice(null);
      return undefined;
    }
    let cancelled = false;
    changedDuringReadRef.current = null;
    (async () => {
      const { locale: accountLocale, error } = await getMyPreferredLocale(userId);
      if (cancelled) return;
      if (error) {
        console.error('Could not read the member\'s language choice:', error);
        setMemberChoice({ userId, locale: null, failed: true });
        return;
      }
      if (changedDuringReadRef.current === userId) return; // chosen in the menu meanwhile — that wins
      const browserChoice = readVisitorChoice();
      if (!accountLocale && browserChoice) {
        const { error: writeError } = await setMyPreferredLocale(userId, browserChoice);
        if (cancelled) return;
        if (writeError) console.error('Could not keep the visitor\'s language choice on the account:', writeError);
        setMemberChoice({ userId, locale: browserChoice });
        return;
      }
      setMemberChoice({ userId, locale: accountLocale });
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // The person's explicit choice: the member's (once read for this member), else the visitor's.
  const memberChoiceForUser = userId && memberChoice?.userId === userId ? memberChoice : null;
  const choice = userId ? (memberChoiceForUser?.locale ?? null) : visitorChoice;
  // A member's account not yet read is undecided, not "no choice": deciding now would show the
  // country default and then switch to the member's own language.
  const memberPending = Boolean(userId) && !memberChoiceForUser;

  const resolvedLocale = memberPending ? null : resolveLocale({ choice, settings, countryCode });
  const locale = resolvedLocale || FALLBACK_LOCALE;

  // English source strings — loaded once.
  useEffect(() => {
    let cancelled = false;
    fetchEnglishStrings().then(eng => { if (!cancelled) setEnglish(eng); });
    return () => { cancelled = true; };
  }, []);

  // Translations for the resolved language — fetched only once the language is decided, so a
  // first visit does not fetch one language and then another.
  useEffect(() => {
    if (!resolvedLocale) return undefined;
    let cancelled = false;
    setReady(false);
    fetchTranslations(resolvedLocale).then(trans => {
      if (!cancelled) {
        setTranslations(trans);
        setReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [resolvedLocale]);

  // The page states its language (R6).
  useEffect(() => {
    if (resolvedLocale) document.documentElement.lang = resolvedLocale;
  }, [resolvedLocale]);

  // An explicit choice (R2′). Compared against the stored choice, not the shown language: choosing
  // the language already shown by default still records the choice, so it holds in other countries.
  const changeLocale = useCallback((newLocale) => {
    if (!newLocale || newLocale === choice) return;
    console.log(`🌐 changeLocale: ${choice || '(none)'} → ${newLocale}`);
    if (userId) {
      changedDuringReadRef.current = userId;
      setMemberChoice({ userId, locale: newLocale });
      setMyPreferredLocale(userId, newLocale).then(({ error }) => {
        if (error) console.error('Could not save the language choice to the account:', error);
      });
    } else {
      try { localStorage.setItem(VISITOR_CHOICE_KEY, newLocale); } catch { /* storage unavailable */ }
      setVisitorChoice(newLocale);
    }
  }, [choice, userId]);

  const value = useMemo(() => ({
    locale,
    formattingLocale: toFormattingLocale(locale),
    english,
    translations,
    changeLocale,
    languages: settings?.languages || [],
    countryInView: countryCode,
    ready
  }), [locale, english, translations, changeLocale, settings, countryCode, ready]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

/** The country in view (R1) — the one value the header and the language rule both read. */
export function useCountryInView() {
  return useContext(TranslationContext).countryInView;
}

/** The locale dates and numbers are formatted in, following the language (A4). */
export function useFormattingLocale() {
  return useContext(TranslationContext).formattingLocale;
}

/** The offered languages, in menu order, by their own names (R5′), and the current choice. */
export function useLanguageMenu() {
  const { languages, locale, changeLocale } = useContext(TranslationContext);
  return { languages, locale, changeLocale };
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * useTranslation hook — provides a `t()` function scoped to a namespace.
 *
 * @param {string} defaultNamespace - e.g. 'auth', 'common', 'articles'
 * @returns {{ t: function, locale: string, formattingLocale: string, changeLocale: function, ready: boolean }}
 */
export function useTranslation(defaultNamespace = 'common') {
  const { locale, formattingLocale, english, translations, changeLocale, ready } = useContext(TranslationContext);

  const t = useCallback((key, variables) => {
    // Support cross-namespace lookup with colon syntax: 'common:cancel'
    let fullKey;
    if (key.includes(':')) {
      const [ns, k] = key.split(':', 2);
      fullKey = `${ns}.${k}`;
    } else if (key.includes('.')) {
      fullKey = key;
    } else {
      fullKey = `${defaultNamespace}.${key}`;
    }

    return resolveTranslation(fullKey, locale, translations, english, variables);
  }, [defaultNamespace, locale, translations, english]);

  return { t, locale, formattingLocale, changeLocale, ready };
}
